/**
 * Investor Dashboard — Portfolio Overview
 * 
 * Shows high-level portfolio metrics: face value, purchase price,
 * collections received, ROI, MOIC, and annualized return.
 * Designed for clear, concise presentation for investor review.
 */
import { useInvestorAuth } from "@/hooks/useInvestorAuth";
import InvestorLayout from "@/components/InvestorLayout";
import { trpc } from "@/lib/trpc";
import { AnimatedCurrency, AnimatedCounter, AnimatedPercentage } from "@/components/AnimatedCounter";
import {
  TrendingUp,
  DollarSign,
  Briefcase,
  BarChart3,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  Activity,
} from "lucide-react";

const BRAND = {
  bg: "#080d16",
  card: "#0e1623",
  cardHover: "#111d2e",
  border: "#1a2540",
  accent: "#3b82f6",
  accentLight: "rgba(59,130,246,0.1)",
  gold: "#f59e0b",
  goldLight: "rgba(245,158,11,0.1)",
  green: "#10b981",
  greenLight: "rgba(16,185,129,0.1)",
  purple: "#8b5cf6",
  purpleLight: "rgba(139,92,246,0.1)",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
};

function MetricCard({
  label,
  value,
  subValue,
  icon: Icon,
  color,
  colorLight,
  trend,
}: {
  label: string;
  value: React.ReactNode;
  subValue?: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  color: string;
  colorLight: string;
  trend?: { value: number; label: string };
}) {
  return (
    <div
      style={{
        background: BRAND.card,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 14,
        padding: "22px 24px",
        transition: "all 0.2s",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = BRAND.cardHover;
        (e.currentTarget as HTMLDivElement).style.borderColor = color + "40";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = BRAND.card;
        (e.currentTarget as HTMLDivElement).style.borderColor = BRAND.border;
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: colorLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={19} color={color} />
        </div>
        {trend && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: trend.value >= 0 ? BRAND.greenLight : "rgba(239,68,68,0.1)",
              color: trend.value >= 0 ? BRAND.green : "#ef4444",
              padding: "3px 8px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <ArrowUpRight size={12} />
            {trend.label}
          </div>
        )}
      </div>
      <div style={{ color: BRAND.text, fontSize: 26, fontWeight: 700, marginBottom: 4, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ color: BRAND.textMuted, fontSize: 13, fontWeight: 500 }}>{label}</div>
      {subValue && (
        <div style={{ color: BRAND.textDim, fontSize: 12, marginTop: 4 }}>{subValue}</div>
      )}
    </div>
  );
}

function PortfolioCard({ portfolio }: { portfolio: any }) {
  const purchasePrice = parseFloat(portfolio.purchase_price || "0");
  const totalSalePrice = parseFloat(portfolio.total_sale_price || "0");
  const purchaseDate = new Date(portfolio.purchase_date);
  const statusColor =
    portfolio.status === "active" ? BRAND.green : portfolio.status === "completed" ? BRAND.accent : BRAND.gold;

  return (
    <div
      style={{
        background: BRAND.card,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 14,
        padding: "20px 22px",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = BRAND.accent + "40";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = BRAND.border;
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h3 style={{ color: BRAND.text, fontSize: 15, fontWeight: 700, margin: "0 0 4px 0" }}>
            {portfolio.name}
          </h3>
          {portfolio.description && (
            <p style={{ color: BRAND.textDim, fontSize: 12, margin: 0 }}>{portfolio.description}</p>
          )}
        </div>
        <span
          style={{
            background: statusColor + "18",
            color: statusColor,
            padding: "3px 10px",
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        >
          {portfolio.status}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.03)",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <div style={{ color: BRAND.textDim, fontSize: 11, marginBottom: 4 }}>Purchase Price</div>
          <div style={{ color: BRAND.text, fontSize: 16, fontWeight: 700 }}>
            ${purchasePrice.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
        </div>
        <div
          style={{
            background: "rgba(255,255,255,0.03)",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <div style={{ color: BRAND.textDim, fontSize: 11, marginBottom: 4 }}>Sale Price (Face)</div>
          <div style={{ color: BRAND.text, fontSize: 16, fontWeight: 700 }}>
            ${totalSalePrice.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginTop: 12,
          color: BRAND.textDim,
          fontSize: 12,
        }}
      >
        <Clock size={12} />
        <span>Purchased {purchaseDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
        {portfolio.final_payment_date && (
          <>
            <span style={{ margin: "0 4px" }}>·</span>
            <span>
              Final payment{" "}
              {new Date(portfolio.final_payment_date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default function InvestorDashboard() {
  const { user, loading: authLoading } = useInvestorAuth({ redirectOnUnauthenticated: true });

  const overviewQuery = trpc.investor.getPortfolioOverview.useQuery(
    { investorId: user?.id ?? 0 },
    { enabled: !!user?.id }
  );

  const portfoliosQuery = trpc.investor.getPortfolios.useQuery(
    { investorId: user?.id ?? 0 },
    { enabled: !!user?.id }
  );

  if (authLoading) {
    return (
      <InvestorLayout>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
          <div style={{ color: BRAND.textMuted, fontSize: 14 }}>Loading...</div>
        </div>
      </InvestorLayout>
    );
  }

  const overview = overviewQuery.data;
  const portfolios = portfoliosQuery.data || [];

  return (
    <InvestorLayout>
      <div style={{ padding: "32px 36px", maxWidth: 1200 }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ color: BRAND.text, fontSize: 24, fontWeight: 700, margin: "0 0 6px 0" }}>
            Portfolio Overview
          </h1>
          <p style={{ color: BRAND.textMuted, fontSize: 14, margin: 0 }}>
            Welcome back, {user?.name || user?.email}. Here's your current investment performance.
          </p>
        </div>

        {/* Top KPI Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
            marginBottom: 28,
          }}
        >
          <MetricCard
            label="Total Invested"
            value={
              overview ? (
                <AnimatedCurrency value={overview.totalPurchasePrice} />
              ) : (
                <span style={{ color: BRAND.textDim }}>—</span>
              )
            }
            icon={DollarSign}
            color={BRAND.accent}
            colorLight={BRAND.accentLight}
          />
          <MetricCard
            label="Total Collected"
            value={
              overview ? (
                <AnimatedCurrency value={overview.totalCollected} />
              ) : (
                <span style={{ color: BRAND.textDim }}>—</span>
              )
            }
            subValue={
              overview && overview.totalProfit >= 0
                ? `+$${overview.totalProfit.toLocaleString("en-US", { maximumFractionDigits: 0 })} profit`
                : undefined
            }
            icon={TrendingUp}
            color={BRAND.green}
            colorLight={BRAND.greenLight}
            trend={
              overview && overview.roi > 0
                ? { value: overview.roi, label: `+${overview.roi.toFixed(1)}% ROI` }
                : undefined
            }
          />
          <MetricCard
            label="MOIC"
            value={
              overview ? (
                <span>
                  <AnimatedCounter value={overview.moic} decimals={2} />x
                </span>
              ) : (
                <span style={{ color: BRAND.textDim }}>—</span>
              )
            }
            subValue="Multiple on Invested Capital"
            icon={BarChart3}
            color={BRAND.purple}
            colorLight={BRAND.purpleLight}
          />
          <MetricCard
            label="Annualized Return"
            value={
              overview ? (
                <AnimatedPercentage value={overview.annualizedReturn} decimals={1} />
              ) : (
                <span style={{ color: BRAND.textDim }}>—</span>
              )
            }
            subValue="Simple annualized return (SAR)"
            icon={Activity}
            color={BRAND.gold}
            colorLight={BRAND.goldLight}
          />
        </div>

        {/* Portfolio count row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            marginBottom: 32,
          }}
        >
          {[
            {
              label: "Total Portfolios",
              value: overview?.portfolioCount ?? 0,
              icon: Briefcase,
              color: BRAND.accent,
            },
            {
              label: "Active Portfolios",
              value: overview?.activePortfolios ?? 0,
              icon: CheckCircle2,
              color: BRAND.green,
            },
            {
              label: "Sale Price (Face)",
              value: overview
                ? `$${(overview.totalSalePrice / 1000).toFixed(0)}K`
                : "—",
              icon: DollarSign,
              color: BRAND.textMuted,
              raw: true,
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                background: BRAND.card,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 12,
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: item.color + "18",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <item.icon size={16} color={item.color} />
              </div>
              <div>
                <div style={{ color: BRAND.text, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
                  {item.raw ? item.value : <AnimatedCounter value={typeof item.value === "number" ? item.value : 0} />}
                </div>
                <div style={{ color: BRAND.textDim, fontSize: 11, marginTop: 3 }}>{item.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Portfolio List */}
        <div>
          <h2 style={{ color: BRAND.text, fontSize: 17, fontWeight: 700, margin: "0 0 16px 0" }}>
            Your Portfolios
          </h2>
          {portfoliosQuery.isLoading ? (
            <div style={{ color: BRAND.textMuted, fontSize: 14, padding: "20px 0" }}>
              Loading portfolios...
            </div>
          ) : portfolios.length === 0 ? (
            <div
              style={{
                background: BRAND.card,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 14,
                padding: "40px",
                textAlign: "center",
              }}
            >
              <Briefcase size={36} color={BRAND.textDim} style={{ marginBottom: 12 }} />
              <div style={{ color: BRAND.textMuted, fontSize: 14 }}>No portfolios assigned yet.</div>
              <div style={{ color: BRAND.textDim, fontSize: 13, marginTop: 4 }}>
                Contact your administrator to get started.
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 16,
              }}
            >
              {portfolios.map((p: any) => (
                <PortfolioCard key={p.id} portfolio={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </InvestorLayout>
  );
}
